<template>
  <v-main>
    <v-container fluid>
      <!-- Ticket Header -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title class="align-start flex-wrap">
              <v-btn icon @click="$router.push('/tickets')" class="mr-2">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <v-icon left class="mt-1">mdi-ticket</v-icon>
              <span v-if="ticket" class="mr-2">{{ ticket.ticket_code }}</span>
              <v-chip
                v-if="ticket && ticket.category"
                small
                color="orange lighten-4"
                text-color="orange darken-3"
                class="mr-2"
              >
                {{ ticket.category.description }}
              </v-chip>
              <v-spacer></v-spacer>
              <!-- Status Selector -->
              <v-select
                v-if="ticket"
                v-model="ticket.status"
                :items="statusOptions"
                label="Status"
                outlined
                dense
                hide-details
                style="max-width: 200px"
                @change="updateStatus"
                :loading="isUpdatingStatus"
                :disabled="isClosed"
              ></v-select>
            </v-card-title>
            <v-card-subtitle v-if="ticket && ticket.user" class="pb-2">
              <v-icon small class="mr-1">mdi-account</v-icon>
              <strong>{{ ticket.user.first_name }} {{ ticket.user.last_name }}</strong>
              <span v-if="ticket.user.phone || ticket.user.email">
                &nbsp;&bull;&nbsp;{{ ticket.user.phone || ticket.user.email }}
              </span>
              &nbsp;&bull;&nbsp;
              <span>Created: {{ ticket.created_at | ddmmyyyy }}</span>
              &nbsp;&bull;&nbsp;
              <v-icon small class="mr-1" color="orange darken-2">mdi-account-check</v-icon>
              <span v-if="ticket.assigned_admin">
                <strong>{{ ticket.assigned_admin.first_name }} {{ ticket.assigned_admin.last_name }}</strong>
              </span>
              <v-chip v-else x-small color="grey lighten-2" text-color="grey darken-1">Unassigned</v-chip>
            </v-card-subtitle>
          </v-card>
        </v-col>
      </v-row>

      <!-- Closed / Resolved Banner -->
      <v-row v-if="ticket && isClosed">
        <v-col md="12">
          <v-alert
            :type="ticket.status === 'resolved' ? 'success' : 'warning'"
            prominent
            border="left"
            :icon="ticket.status === 'resolved' ? 'mdi-check-circle' : 'mdi-lock'"
          >
            <strong>Ticket {{ statusLabel(ticket.status) }}</strong> — No new messages can be sent.
          </v-alert>
        </v-col>
      </v-row>

      <!-- Chat Area -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text style="min-height: 420px">
              <section v-if="isLoading" style="height: 300px">
                <v-progress-linear indeterminate color="primary"></v-progress-linear>
              </section>
              <div v-else>
                <div v-if="messages.length === 0" class="text-center py-10 grey--text">
                  <v-icon size="64" color="grey lighten-2">mdi-chat-outline</v-icon>
                  <p class="mt-2 text-body-1">No messages yet.</p>
                </div>
                <div
                  v-else
                  class="chat-messages pa-4"
                  ref="chatBox"
                  style="max-height: 520px; overflow-y: auto"
                >
                  <div
                    v-for="msg in messages"
                    :key="msg.id"
                    class="mb-4 d-flex"
                    :class="isMine(msg) ? 'justify-end' : 'justify-start'"
                  >
                    <div
                      class="chat-bubble"
                      :class="isMine(msg) ? 'bubble-admin' : 'bubble-user'"
                    >
                      <div class="bubble-sender mb-1">
                        <span v-if="isMine(msg)">You (Admin)</span>
                        <span v-else-if="msg.sender">
                          {{ msg.sender.first_name }} {{ msg.sender.last_name }}
                        </span>
                        <span v-else>Customer</span>
                      </div>
                      <div class="bubble-text">{{ msg.message }}</div>
                      <div class="bubble-time">
                        {{ msg.created_at | ddmmyyyy }}
                        <v-icon v-if="isMine(msg)" x-small class="ml-1" :color="msg.is_read ? 'white' : 'rgba(255,255,255,0.5)'">
                          mdi-check-all
                        </v-icon>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </v-card-text>

            <!-- Message Input -->
            <v-card-actions v-if="ticket && !isClosed && canReply" class="px-4 pb-4">
              <v-text-field
                v-model="newMessage"
                label="Type a message..."
                outlined
                dense
                hide-details
                @keydown.enter.prevent="sendMessage"
              ></v-text-field>
              <v-btn
                color="primary"
                class="ml-2"
                :loading="isSending"
                @click="sendMessage"
                :disabled="!newMessage.trim()"
              >
                <v-icon>mdi-send</v-icon>
              </v-btn>
            </v-card-actions>

            <!-- Other admin read-only notice -->
            <v-card-actions v-else-if="ticket && !isClosed && !canReply" class="px-4 pb-4 justify-center">
              <v-chip color="orange lighten-4" text-color="orange darken-3">
                <v-icon left small>mdi-eye-outline</v-icon>
                Assigned to {{ ticket.assigned_admin ? ticket.assigned_admin.first_name + ' ' + ticket.assigned_admin.last_name : 'another admin' }} — Read only
              </v-chip>
            </v-card-actions>

            <!-- Closed Footer -->
            <v-card-actions v-else-if="ticket && isClosed" class="px-4 pb-4 justify-center">
              <v-chip color="grey darken-1" text-color="white">
                <v-icon left small>mdi-lock</v-icon>
                Ticket {{ statusLabel(ticket.status) }} — Chat disabled
              </v-chip>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
export default {
  name: "TicketIdPage",
  data() {
    return {
      ticket: null,
      messages: [],
      newMessage: "",
      isLoading: false,
      isSending: false,
      isUpdatingStatus: false,
      statusOptions: [
        { text: "Open", value: "open" },
        { text: "Pending", value: "pending" },
        { text: "Resolved", value: "resolved" },
        { text: "Closed", value: "closed" },
      ],
    };
  },
  computed: {
    isClosed() {
      return this.ticket && (this.ticket.status === "resolved" || this.ticket.status === "closed");
    },
    canReply() {
      if (!this.ticket) return false;
      // Customer can always reply on their own ticket
      if (!this.ticket.assigned_to) return true; // unassigned — anyone can reply
      // If assigned, only the assigned admin can reply
      return this.ticket.assigned_to === this.user.id || this.ticket.user_id === this.user.id;
    },
    rawTicketId() {
      return this.decryptIt(this.$route.params.ticketId);
    },
  },
  mounted() {
    this.loadAll();
  },
  methods: {
    statusLabel(status) {
      const map = { open: "Open", pending: "Pending", resolved: "Resolved", closed: "Closed" };
      return map[status] || status;
    },
    isMine(msg) {
      return msg.sender_id === this.user.id || msg.sender_id == this.user.id;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.chatBox) {
          this.$refs.chatBox.scrollTop = this.$refs.chatBox.scrollHeight;
        }
      });
    },
    async loadAll() {
      await this.getTicket();
    },
    async getTicket() {
      this.isLoading = true;
      const encryptedId = this.$route.params.ticketId;
      try {
        const res = await axios.get(`tickets/${encryptedId}`);
        this.ticket = res.data.data;
        // Messages are loaded with ticket in show() response
        this.messages = (this.ticket && this.ticket.messages) ? this.ticket.messages : [];
        this.scrollToBottom();
      } catch (e) {
        console.log(e);
      }
      this.isLoading = false;
    },
    async getMessages() {
      try {
        const res = await axios.get(`ticket-messages?ticket_id=${this.rawTicketId}`);
        this.messages = res.data.data || [];
        this.scrollToBottom();
      } catch (e) {
        console.log(e);
      }
    },
    async sendMessage() {
      if (!this.newMessage.trim()) return;
      this.isSending = true;
      try {
        const res = await axios.post("ticket-messages", {
          ticket_id: this.rawTicketId,
          message: this.newMessage.trim(),
        });
        this.newMessage = "";
        // Append new message and update ticket status if changed
        if (res.data.data) {
          this.messages.push(res.data.data);
          this.scrollToBottom();
        }
        if (res.data.ticket && this.ticket) {
          this.ticket.status        = res.data.ticket.status;
          this.ticket.assigned_to   = res.data.ticket.assigned_to;
          this.ticket.assigned_admin = res.data.ticket.assigned_admin;
        }
      } catch (e) {
        if (e.response && e.response.data && e.response.data.message) {
          alert(e.response.data.message);
        }
        console.log(e);
      }
      this.isSending = false;
    },
    async updateStatus() {
      this.isUpdatingStatus = true;
      try {
        await axios.patch(`tickets/${this.rawTicketId}`, {
          status: this.ticket.status,
        });
      } catch (e) {
        if (e.response && e.response.data && e.response.data.message) {
          alert(e.response.data.message);
        }
        console.log(e);
      }
      this.isUpdatingStatus = false;
    },
  },
};
</script>

<style scoped>
.chat-bubble {
  max-width: 62%;
  padding: 10px 14px;
  border-radius: 18px;
  font-size: 14px;
  word-break: break-word;
}
.bubble-admin {
  background: linear-gradient(135deg, #ff8c00, #ff5722);
  color: white;
  border-bottom-right-radius: 4px;
}
.bubble-user {
  background-color: #f5f5f5;
  color: #333;
  border-bottom-left-radius: 4px;
}
.bubble-sender {
  font-size: 11px;
  font-weight: 700;
  opacity: 0.8;
}
.bubble-text {
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
}
.bubble-time {
  font-size: 10px;
  opacity: 0.65;
  margin-top: 4px;
  text-align: right;
}
</style>
